// Compare (GL-27). Owner and AM, one company.
// GV Books' booked account vs the other software's account vs a shadow AI
// proposal. The comparison table holds account names only: no amounts, no
// dates, no descriptions. The list therefore shows the transaction id and
// links to Activity, where the transaction itself is.

import {
  AdminApiError,
  type AdminClient,
  type BookingComparison,
  type ComparisonPath,
} from "../api.js";
import { csvField } from "../csv_field.js";
import { clear, h } from "../dom.js";
import type { CompanySlug, RouteName } from "../types.js";
import { countOrMoreText, countText } from "../ui/display-words.js";
import * as face from "../ui/face.js";

const MAX_PAGES = 20;
const MONTH_RE = /^\d{4}-(?:0[1-9]|1[0-2])$/;

export const PATH_ORDER: readonly ComparisonPath[] = ["import", "rule", "hub", "ai", "person"];

const PATH_LABEL: Readonly<Record<ComparisonPath, string>> = {
  import: "Imported match",
  rule: "A saved rule",
  // rd round-11 decision 2: a source label names no company, so the owner's
  // allowed word for the practice's records is "hub".
  hub: "Hub records",
  ai: "Suggested",
  person: "A person",
};

function showDetails(detail: string): HTMLElement {
  return h("details", { class: "disclosure", "data-role": "show-details" }, [
    h("summary", {}, ["Show details"]),
    h("p", {}, [detail]),
  ]);
}

export interface PathSummary {
  path: ComparisonPath | "none";
  compared: number;
  agree: number;
}

export interface CompareSummary {
  compared: number;
  agree: number;
  disagree: number;
  withAi: number;
  aiAgreesImported: number;
  aiAgreesBooked: number;
  byPath: PathSummary[];
}

/** GV Books agrees with the other software when the accounts are equal. */
export function agrees(row: BookingComparison): boolean {
  return row.bookedAccount !== null && row.bookedAccount === row.importedAccount;
}

export function summarize(rows: readonly BookingComparison[]): CompareSummary {
  const byPath = new Map<ComparisonPath | "none", PathSummary>();
  const summary: CompareSummary = {
    compared: rows.length,
    agree: 0,
    disagree: 0,
    withAi: 0,
    aiAgreesImported: 0,
    aiAgreesBooked: 0,
    byPath: [],
  };
  for (const row of rows) {
    const same = agrees(row);
    if (same) summary.agree += 1;
    else summary.disagree += 1;
    if (row.aiAccount !== null) {
      summary.withAi += 1;
      if (row.aiAccount === row.importedAccount) summary.aiAgreesImported += 1;
      if (row.bookedAccount !== null && row.aiAccount === row.bookedAccount) {
        summary.aiAgreesBooked += 1;
      }
    }
    const key = row.bookedPath ?? "none";
    const entry = byPath.get(key) ?? { path: key, compared: 0, agree: 0 };
    entry.compared += 1;
    if (same) entry.agree += 1;
    byPath.set(key, entry);
  }
  summary.byPath = [...PATH_ORDER, "none" as const]
    .map((key) => byPath.get(key))
    .filter((entry): entry is PathSummary => entry !== undefined);
  return summary;
}

export function disagreements(rows: readonly BookingComparison[]): BookingComparison[] {
  return rows.filter((row) => !agrees(row));
}

/** CSV of every comparison. Accounts and ids only; no amounts, no memos. */
export function comparisonsCsv(rows: readonly BookingComparison[]): string {
  const header =
    "transaction_id,imported_account,booked_account,booked_path,ai_account,ai_confidence,agrees";
  const lines = rows.map((row) =>
    [
      row.plaidTransactionId,
      row.importedAccount,
      row.bookedAccount ?? "",
      row.bookedPath ?? "",
      row.aiAccount ?? "",
      row.aiConfidence ?? "",
      agrees(row) ? "yes" : "no",
    ]
      .map(csvField)
      .join(","),
  );
  return [header, ...lines].join("\n");
}

function percent(part: number, whole: number): string {
  if (whole === 0) return "—";
  return `${String(Math.round((part / whole) * 100))}%`;
}

export interface ComparePageOptions {
  client: AdminClient;
  company: CompanySlug;
  root: HTMLElement;
  isCurrent?: () => boolean;
  navigateTo?: (route: RouteName | "system") => void | Promise<void>;
  /**
   * The shell's company names (slug -> name), from which the page builds its
   * ONE account-label context (rd13 L1). Compare names three accounts a row,
   * so a between-companies path says the company, never its internal code.
   */
  companyLabels?: ReadonlyMap<CompanySlug, string>;
  /** The shell's ledger segment to company name read (`face.companySegmentNames`, rd13 L1). */
  companySegments?: ReadonlyMap<string, string>;
}

export async function renderComparePage(options: ComparePageOptions): Promise<void> {
  const { client, company, root } = options;
  // The page's ONE label context, built once from the shell's names (rd13 L1).
  const companies = face.accountLabelContext(options.companyLabels, options.companySegments);
  const isCurrent = options.isCurrent ?? ((): boolean => true);
  clear(root);
  const tiles = h("div", { class: "tiles", "data-role": "compare-tiles" });
  const pathHost = h("div", { "data-role": "compare-paths" });
  const listHost = h("div", { "data-role": "compare-list" });
  const status = h("p", { class: "muted", role: "status", "data-role": "compare-status" }, [
    "Loading comparisons…",
  ]);
  const monthInput = h("input", {
    type: "text",
    class: "field-input",
    inputmode: "numeric",
    placeholder: "YYYY-MM",
    "aria-label": "Month (optional)",
    "data-role": "compare-month",
  });
  const apply = h(
    "button",
    { type: "button", class: "btn btn-primary", "data-role": "compare-apply" },
    ["Apply"],
  );
  const retry = h(
    "button",
    { type: "button", class: "btn btn-primary", "data-action": "retry", hidden: "true" },
    ["Try again"],
  );
  const csvHost = h("div", { "data-role": "compare-csv-host" });
  root.append(
    h("header", { class: "page-header" }, [
      h("h1", { class: "page-title" }, ["Compare"]),
      h("p", { class: "lede" }, [
        "Where GV Books and the other software agree. Nothing here changes a booking.",
      ]),
    ]),
    h("div", { class: "toolbar" }, [
      h("label", { class: "field" }, [
        h("span", { class: "field-label" }, ["Month, if you want one"]),
        monthInput,
      ]),
      apply,
      retry,
      csvHost,
    ]),
    status,
    tiles,
    pathHost,
    listHost,
  );

  let csvUrl: string | null = null;

  const load = async (): Promise<void> => {
    const month = monthInput.value.trim();
    if (month !== "" && !MONTH_RE.test(month)) {
      status.textContent = "Month must look like 2026-03.";
      return;
    }
    status.textContent = "Loading comparisons…";
    retry.hidden = true;
    retry.setAttribute("hidden", "true");
    const rows: BookingComparison[] = [];
    let cursor: string | undefined;
    let truncated = false;
    try {
      for (let page = 0; page < MAX_PAGES; page += 1) {
        const data = await client.listBookingComparisons(company, {
          ...(month === "" ? {} : { month }),
          ...(cursor === undefined ? {} : { cursor }),
        });
        if (!isCurrent()) return;
        rows.push(...data.comparisons);
        if (data.nextCursor === null) break;
        cursor = data.nextCursor;
        if (page === MAX_PAGES - 1) truncated = true;
      }
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof AdminApiError && error.status === 403) {
        status.textContent = "This company is not eligible for comparisons.";
        retry.hidden = true;
        retry.setAttribute("hidden", "true");
      } else {
        status.textContent = "Comparisons could not be loaded.";
        retry.hidden = false;
        retry.removeAttribute("hidden");
      }
      return;
    }
    paint(rows, truncated);
  };

  const paint = (rows: readonly BookingComparison[], truncated: boolean): void => {
    clear(tiles);
    clear(pathHost);
    clear(listHost);
    clear(csvHost);
    if (csvUrl !== null) {
      URL.revokeObjectURL(csvUrl);
      csvUrl = null;
    }
    if (rows.length === 0) {
      status.textContent =
        "No comparisons yet. They appear after a re-import matches transactions GV Books has already booked. Upload the other software's file, then come back.";
      return;
    }
    status.textContent = truncated
      ? "Showing the first part of the comparisons. Narrow by month to see the rest."
      : "";
    // Rows are read page by page up to a bound; past it every count is a floor.
    const complete = !truncated;
    const s = summarize(rows);
    const tile = (role: string, label: string, value: string): HTMLElement =>
      h("div", { class: "tile", "data-role": role }, [
        h("div", { class: "tile-value" }, [value]),
        h("div", { class: "tile-label muted" }, [label]),
      ]);
    tiles.append(
      tile("tile-compared", "Compared", countText(s.compared, complete)),
      tile("tile-agree", "GV Books agrees with the other software", `${countText(s.agree, complete)} (${percent(s.agree, s.compared)})`),
      tile("tile-disagree", "Disagreements", countText(s.disagree, complete)),
      tile("tile-ai", "With an AI proposal", countText(s.withAi, complete)),
      tile("tile-ai-imported", "AI agrees with the other software", `${countText(s.aiAgreesImported, complete)} of ${countText(s.withAi, complete)}`),
      tile("tile-ai-booked", "AI agrees with GV Books", `${countText(s.aiAgreesBooked, complete)} of ${countText(s.withAi, complete)}`),
    );

    const pathTable = h("table", { class: "table data-table" }, [
      h("thead", {}, [
        h("tr", {}, [
          h("th", {}, ["Booked by"]),
          h("th", { class: "num" }, ["Compared"]),
          h("th", { class: "num" }, ["Agree"]),
          h("th", { class: "num" }, ["Rate"]),
        ]),
      ]),
      h(
        "tbody",
        {},
        s.byPath.map((entry) =>
          h("tr", { "data-path": entry.path }, [
            h("td", {}, [entry.path === "none" ? "Other" : PATH_LABEL[entry.path]]),
            h("td", { class: "num" }, [countText(entry.compared, complete)]),
            h("td", { class: "num" }, [countText(entry.agree, complete)]),
            h("td", { class: "num" }, [percent(entry.agree, entry.compared)]),
          ]),
        ),
      ),
    ]);
    pathHost.append(
      h("h3", {}, ["Agreement by how it was booked"]),
      h("div", { class: "table-scroll" }, [pathTable]),
    );

    const bad = disagreements(rows);
    listHost.append(h("h3", {}, [`Disagreements (${countOrMoreText(bad.length, complete)})`]));
    if (bad.length === 0) {
      listHost.append(
        h("p", { class: "muted empty-state" }, [
          // "Every comparison agrees" is a claim about every comparison, and a
          // scan that stopped at the page bound has not seen them all
          // (rd Z4, carried from round 8).
          complete
            ? "None. Every comparison agrees. Nothing to fix on this list."
            : "None found in what was checked. The scan was cut short.",
        ]),
      );
      if (!complete) {
        // A cut-short scan is not a dead end: ask again, or narrow by month.
        retry.hidden = false;
        retry.removeAttribute("hidden");
      }
    } else {
      const disagreementTable = h("table", { class: "table data-table" }, [
          h("thead", {}, [
            h("tr", {}, [
              h("th", {}, ["Transaction"]),
              h("th", {}, ["Other software"]),
              h("th", {}, ["GV Books"]),
              h("th", {}, ["Booked by"]),
              h("th", {}, ["AI"]),
              h("th", {}, [""]),
            ]),
          ]),
          h(
            "tbody",
            {},
            bad.map((row) => {
              const open = h(
                "button",
                { type: "button", class: "btn btn-secondary", "data-role": "open-transaction" },
                ["Open in Activity"],
              );
              open.addEventListener("click", () => {
                void options.navigateTo?.("transactions");
              });
              const suggestion =
                row.aiAccount === null ? "—" : face.account(row.aiAccount, companies);
              return h("tr", { "data-txn-id": row.plaidTransactionId }, [
                h("td", {}, [
                  "Transaction",
                  showDetails(row.plaidTransactionId),
                ]),
                h("td", {}, [face.account(row.importedAccount, companies)]),
                h("td", {}, [
                  row.bookedAccount === null ? "—" : face.account(row.bookedAccount, companies),
                ]),
                h("td", {}, [row.bookedPath === null ? "—" : PATH_LABEL[row.bookedPath]]),
                h("td", {}, [
                  suggestion,
                  row.aiAccount !== null && row.aiConfidence !== null
                    ? showDetails(row.aiConfidence)
                    : null,
                ]),
                h("td", {}, [open]),
              ]);
            }),
          ),
        ]);
      listHost.append(h("div", { class: "table-scroll" }, [disagreementTable]));
    }

    csvUrl = URL.createObjectURL(new Blob([comparisonsCsv(rows)], { type: "text/csv" }));
    csvHost.append(
      h("a", { href: csvUrl, download: "comparisons.csv", "data-role": "compare-csv" }, [
        "Download CSV",
      ]),
    );
  };

  apply.addEventListener("click", () => {
    void load();
  });
  retry.addEventListener("click", () => {
    void load();
  });
  await load();
}
